import cn from 'classnames'
import { ICommitmentReminderActionItem } from 'legacy/lut/services/partner360/commitment/models/ICommitmentReminder'
import { useCallback, useMemo } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import {
  AbsolutePopup,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'
import { PartnerCommitmentTranslations } from 'partner360/entities/partnerCompleteness'

import { ReactComponent as LockIcon } from './assets/LockIcon.svg'

import styles from './ActionsPopup.module.scss'

interface IActionsPopupProps {
  isLoading: boolean
  actions: ICommitmentReminderActionItem[]
  onSelectAction: (actionType: ICommitmentReminderActionItem) => void
  selectedAction: ICommitmentReminderActionItem | null
  shortView?: boolean
  isDisabled?: boolean
}

export const ActionsPopup = ({
  isLoading,
  actions,
  onSelectAction,
  selectedAction,
  shortView,
  isDisabled,
}: IActionsPopupProps) => {
  const t = useSafetyTranslation()

  const sortedActions = useMemo(
    () =>
      [...actions].sort(
        (a, b) =>
          (a.ActionOrder ?? Number.MAX_SAFE_INTEGER) - (b.ActionOrder ?? Number.MAX_SAFE_INTEGER)
      ),
    [actions]
  )

  const renderBlockedBy = useCallback(
    (blockedBy?: string) => {
      if (!blockedBy) {
        return <div />
      }

      const blockedActions = blockedBy
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => t(item))
        .join(', ')

      return (
        <div className={styles.tooltipContent}>
          <Translation
            span
            text={PartnerCommitmentTranslations.BlockedActions}
            replaceParams={[
              {
                key: '{actions}',
                value: blockedActions,
              },
            ]}
          />
        </div>
      )
    },
    [t]
  )

  const visibilityCallback = useCallback(
    (newVisible: boolean) => {
      if (selectedAction) {
        return false
      }
      return newVisible
    },
    [selectedAction]
  )

  return (
    <AbsolutePopup
      verticalDirection={TPopupVertical.Bottom}
      horizontalStartPosition={TPopupHorizontal.Left}
      horizontalDirection={TPopupHorizontal.Right}
      verticalStartPosition={TPopupVertical.Bottom}
      visibilityTrigger={TVisibilityTrigger.Click}
      visibilityCallback={visibilityCallback}
      popupContent={() => {
        return (
          <div className={styles.popup}>
            {sortedActions.map((item) => {
              const isBlocked = Boolean(item.ActionBlockedBy)

              const handleItemClick = () => {
                onSelectAction(item)
              }

              return (
                <div
                  key={item.ActionTitle}
                  className={cn(styles.item, {
                    [styles.itemBlocked]: isBlocked || isDisabled,
                    [styles.item__loading]: isLoading,
                  })}
                  onClick={isBlocked || isDisabled ? undefined : handleItemClick}
                >
                  <div className={styles.itemInner}>
                    <div className={styles.itemContent}>
                      <div className={styles.itemTitle}>
                        <Translation text={item.ActionTitle} />
                      </div>

                      {item.ActionDescription && (
                        <div className={styles.itemDescription}>
                          <Translation text={item.ActionDescription} />
                        </div>
                      )}
                    </div>

                    {isBlocked ? (
                      <div className={styles.itemEndIcon}>
                        <Tooltip
                          verticalDirection={TPopupVertical.Bottom}
                          tipContent={() => renderBlockedBy(item.ActionBlockedBy)}
                        >
                          <LockIcon className={styles.lockIcon} />
                        </Tooltip>
                      </div>
                    ) : (
                      <div className={styles.itemEndIcon}>
                        <i className={cn('kbase kbase-arrow-right', styles.arrowIcon)} />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )
      }}
    >
      <Button disabled={isLoading} className={styles.btn} variant={'text'}>
        {isLoading ? (
          <Loader dimension={{ height: 25, width: 25 }} />
        ) : (
          <div className={styles.btn__content}>
            <RenderWhen condition={!shortView}>
              <Translation text={PartnerCommitmentTranslations.Review} />
            </RenderWhen>

            <span>({actions.length})</span>
            <i className={cn('kbase kbase-omni-arrows-right', styles.btn__content__icon)} />
          </div>
        )}
      </Button>
    </AbsolutePopup>
  )
}
